import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown in place of the crashed subtree. `null` for a silent, contained failure (e.g. a
   *  non-critical widget); a visible fallback for anything the visitor would otherwise think
   *  the whole app had died. */
  fallback: ReactNode;
  /** Named per instance so a crash log identifies which boundary caught it. */
  name: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains a render crash to the subtree it wraps, instead of taking the whole app down. React
 * only offers this as a class component — there's no hook equivalent — so this is the one class
 * component in the app, and it exists purely for that reason.
 *
 * Two boundaries use this:
 *   - Root, in main.tsx: any unexpected crash anywhere shows a "something went wrong, reload"
 *     screen instead of a blank white page.
 *   - Around <PromoSlot>: ad placements are non-critical and third-party-dependent (blocked by
 *     ad blockers, occasionally by AdSense itself) — a failure there must never be visible to a
 *     player trying to make a pick.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[error-boundary:${this.props.name}]`, error, info.componentStack);
  }

  render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
